import { useEffect, useRef } from 'react';

const services = [
  {
    id: 'service-p2p',
    title: "Peer to Peer Texting",
    description: "Reach people quickly, boost response rates, and drive action. Peer-to-peer texting delivers personalized, one-to-one messages that spark real conversations.",
    features: [
      "SMS up to 160 characters",
      "MMS. High resolution videos. Up to 1400 characters",
      "Real time link tracking",
      "Multi language",
      "Spam Checks",
      "10DLC Support"
    ],
    image: "/images/services/p2p-cut-out.png",
    side: 'left'
  },
  {
    id: 'service-auto-call',
    title: "Automated Phone Calls",
    description: "Share important updates, reminders, and calls to action with consistent messaging and reliable reach. Automated phone calls deliver pre-recorded messages quickly and efficiently to large audiences.",
    features: [
      "Callerid Customization",
      "Opt Out Management",
      "Detailed Call Reporting",
      "Professional Voice Options",
      "Simple Setup & Management"
    ],
    image: "/images/services/autocall.jpeg",
    side: 'right'
  },
  {
    id: 'service-survey',
    title: "Surveys and Polling",
    description: "Gather feedback, measure sentiment, and understand your audience through automated phone calls, live agent calls, or text messaging. Reach people where they're most responsive and collect reliable, actionable insights.",
    features: [
      "Real-Time Reporting – Monitor results as responses come in",
      "Custom Question Design – Tailor questions to meet your goals",
      "Scalable – Quickly collect responses from large audiences",
      "Skip Logic & Branching – Customize question paths based on responses",
      "Data Export & Integration – Easily export results for analysis and reporting",
      "Compliance & Opt-Out Handling – Built-in safeguards for outreach regulations"
    ],
    image: "/images/services/survey-and-polling.png",
    side: 'left'
  },
  {
    id: 'service-agent',
    title: "Live Agent Calls",
    description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Morbi purus lacus, hendrerit vel efficitur ut, pellentesque id nisl.",
    image: "/images/services/agent-service.jpg",
    side: 'right'
  },
  {
    id: 'service-tele-townhall',
    title: "Tele-Townhalls",
    summary: "Host impactful large donor conference calls to connect with up to 50,000 supporters per minute. Empower your non-profit clients to engage with their donors in a meaningful way and foster a sense of community among supporters.",
    features: [
      "Connect with up to 50,000 supporters per minute",
      "Enhanced transparency and accountability",
      "Increased donor engagement",
      "Real-time feedback and support"
    ],
    image: "/images/services/townhall-service.jpg",
    side: 'left'
  },
  {
    id: 'service-data',
    title: "Registered Voter, Donor & Consumer Data",
    description: "Registered voter, donor and consumer data provides accurate, up-to-date information to help organizations identify, understand, and reach the right audiences. With reliable data and targeting options, outreach efforts become more efficient, focused, and effective.",
    features: [
      "Phone records enriched with first and last name, address, and email",
      "Demographic filters including age and gender, hobbies and interests",
      "Registered voter history and participation data"
    ],
    image: "/images/services/data.png",
    side: 'right'
  }
];

const Services = () => {
  const servicesListRef = useRef(null);

  useEffect(() => {
    // Apply reveal animation to section title
    const sectionTitle = document.querySelector('#services h2');
    if (sectionTitle) {
      sectionTitle.classList.add('reveal');
      const revealObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      }, {
        threshold: 0.15,
        rootMargin: '0px 0px -80px 0px'
      });
      revealObserver.observe(sectionTitle);
    }

    // Apply slide-in animations to service cards
    if (servicesListRef.current) {
      const serviceCards = servicesListRef.current.querySelectorAll('[data-service-card]');
      
      const slideInObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('slide-in');
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateX(0)';
            slideInObserver.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.15,
        rootMargin: '0px 0px -100px 0px'
      });

      serviceCards.forEach(card => {
        slideInObserver.observe(card);
      });
    }
  }, []);

  return (
    <section
      id="services"
      className="py-12 md:py-16 lg:py-24 px-4 sm:px-6 lg:px-8 m-0 bg-[var(--bg-charcoal)] relative overflow-hidden z-[3] w-full max-w-[100vw]"
    >
      <div className="max-w-[1200px] mx-auto w-full overflow-hidden">
        {/* Section Title */}
        <h2
          className="text-center text-3xl md:text-4xl lg:text-5xl mb-8 md:mb-12 lg:mb-16 text-[var(--bg-pure-white)] relative block w-full font-semibold uppercase tracking-[4px] font-serif border-b-0 pb-6 after:content-[''] after:absolute after:-bottom-[10px] after:left-1/2 after:-translate-x-1/2 after:w-[150px] sm:after:w-[200px] md:after:w-[250px] after:h-[4px] sm:after:h-[5px] md:after:h-[6px] after:bg-[var(--accent-color)]"
        >
          Our Services
        </h2>

        {/* Services List - flex column layout */}
        <div 
          ref={servicesListRef}
          className="flex flex-col gap-8 md:gap-10 lg:gap-12 mt-0 relative z-[1] w-full items-center"
        >
          {services.map((service) => {
            const isLeft = service.side === 'left';
            
            return (
              <div 
                key={service.id}
                id={service.id}
                data-service-card
                className={`
                  bg-[var(--bg-pure-white)] text-[var(--text-dark)] p-0 rounded-xl
                  transition-all duration-[800ms] ease-[cubic-bezier(0.4,0,0.2,1)] cursor-pointer
                  relative overflow-hidden border border-[var(--border-light)] z-[1]
                  shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.04)]
                  flex items-stretch w-full max-w-[850px] mx-auto gap-0 opacity-0
                  flex-col md:flex-row
                  ${isLeft ? 'md:flex-row md:-translate-x-[100px]' : 'md:flex-row-reverse md:translate-x-[100px]'}
                  ${service.id === 'service-tele-townhall' ? 'items-start' : ''}
                  ${service.id === 'service-survey' ? 'md:items-stretch' : 'min-h-[350px]'}
                `}
                style={{
                  transition: 'opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1), transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
                onMouseEnter={(e) => {
                  if (e.currentTarget.classList.contains('slide-in')) {
                    e.currentTarget.style.transform = 'translateY(-6px) translateX(0)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (e.currentTarget.classList.contains('slide-in')) {
                    e.currentTarget.style.transform = 'translateX(0)';
                  }
                }}
              >
                {/* Service Image */}
                <div className={`
                  w-full flex-shrink-0
                  ${service.id === 'service-survey' 
                    ? 'md:w-[500px] md:min-w-[500px] md:max-w-[500px] md:h-full md:self-stretch md:flex md:items-center md:justify-center' 
                    : service.id === 'service-p2p'
                    ? 'md:w-[650px] md:min-w-[650px] md:max-w-[650px] h-full min-h-[350px]'
                    : service.id === 'service-auto-call'
                    ? 'md:w-[550px] md:min-w-[550px] md:max-w-[550px] h-full min-h-[350px]'
                    : service.id === 'service-data'
                    ? 'md:w-[550px] md:min-w-[550px] md:max-w-[550px] h-full min-h-[350px]'
                    : 'md:w-[380px] md:min-w-[380px] md:max-w-[380px] h-full min-h-[350px]'}
                  ${isLeft ? 'rounded-l-xl rounded-r-0 overflow-hidden' : 'rounded-r-xl rounded-l-0 overflow-hidden'}
                  ${service.id === 'service-survey' ? 'pt-8 md:pt-12' : ''}
                `}>
                  <img
                    src={service.image}
                    alt={service.title}
                    className={`
                      block
                      ${service.id === 'service-survey' 
                        ? 'w-full h-full object-contain object-center' 
                        : service.id === 'service-p2p'
                        ? 'w-full h-full object-contain object-center'
                        : 'w-full h-full object-cover'}
                    `}
                  />
                </div>

                {/* Service Content */}
                <div 
                  className={`
                    flex-1 p-5 md:p-6 lg:p-8 flex flex-col
                    ${service.id === 'service-tele-townhall' ? 'justify-start' : 'justify-center'}
                    ${service.id === 'service-tele-townhall' ? 'pb-5 md:pb-6 lg:pb-8' : ''}
                    ${service.id === 'service-survey' ? 'md:justify-start md:overflow-y-auto md:self-stretch md:h-full' : ''}
                  `}
                >
                  {/* Service Title */}
                  <h3 className="text-xl md:text-2xl lg:text-3xl mb-1 mt-0 font-semibold uppercase tracking-[2px] text-[var(--text-dark)]">
                    {service.title}
                  </h3>

                  {/* Service Summary (for tele-townhall) */}
                  {service.summary && (
                    <p className="text-[var(--text-medium)] leading-[1.7] text-base md:text-lg mb-6 md:mb-10 lg:mb-10 mt-0 pt-0">
                      {service.summary}
                    </p>
                  )}

                  {/* Service Description */}
                  {service.description && (
                    <p className={`text-[var(--text-medium)] leading-[1.7] text-sm md:text-base mt-2 ${service.features && service.features.length > 0 ? 'mb-4 md:mb-6' : ''}`}>
                      {service.description}
                    </p>
                  )}

                  {/* Service Features List (for tele-townhall and other services) */}
                  {service.features && service.features.length > 0 && (
                    <ul className="list-none p-0 m-0 text-[var(--text-medium)] leading-[1.8] text-base md:text-lg mt-0 mb-0">
                      {service.features.map((feature, idx) => (
                        <li 
                          key={idx}
                          className="relative pl-9 mb-3 text-base md:text-lg leading-[1.25rem] pt-0 mt-[-0.2rem] last:mb-0
                            before:content-['✓'] before:absolute before:left-0 before:top-[-0.05rem]
                            before:w-5 before:h-5 before:bg-[var(--accent-color)] before:text-[var(--text-dark)]
                            before:flex before:items-center before:justify-center before:text-sm before:font-bold
                            before:rounded before:leading-none before:z-[1]
                            after:content-[''] after:absolute after:left-0 after:top-[-0.05rem]
                            after:w-full after:h-5 after:bg-[var(--bg-light)] after:rounded after:z-[-1]
                          "
                        >
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

    </section>
  );
};

export default Services;
